export const AUTH_STORAGE_KEYS = {
    accessToken: 'access_token',
    accessTokenTimestamp: 'access_token_timestamp',
    impersonatorAccessToken: 'impersonator_access_token',

    hostApp: 'app',
    hostSupport: 'support',
    hostTheme: 'theme',
    hostLogo: 'logo',
    hostWatermark: 'watermark',
    hostFavicon: 'favicon',

    user: 'user',
    organizations: 'organizations',
    access: 'access',
    selectedOrganization: 'o-s',
    selectedBranch: 'b-s',
    impersonatorSelectedOrganization: 'o-i-s',
    impersonatorSelectedBranch: 'b-i-s'
} as const;

export type AuthStorageKey = (typeof AUTH_STORAGE_KEYS)[keyof typeof AUTH_STORAGE_KEYS];

const APP_SETTINGS_STORAGE_KEY = 'app-settings';

type HostWindowSettings = {
    app?: unknown;
    support?: unknown;
    layout?: {
        theme?: string;
        logo?: string;
        watermark?: string;
        favicon?: string;
    };
    user?: unknown;
    organization?: unknown;
    branch?: unknown;
};

const APP_SETTINGS_KEY_MAP: Partial<Record<AuthStorageKey, string>> = {
    [AUTH_STORAGE_KEYS.hostApp]: 'app',
    [AUTH_STORAGE_KEYS.hostSupport]: 'support',
    [AUTH_STORAGE_KEYS.hostTheme]: 'layout.theme',
    [AUTH_STORAGE_KEYS.hostLogo]: 'layout.logo',
    [AUTH_STORAGE_KEYS.hostWatermark]: 'layout.watermark',
    [AUTH_STORAGE_KEYS.hostFavicon]: 'layout.favicon',
    [AUTH_STORAGE_KEYS.user]: 'user',
    [AUTH_STORAGE_KEYS.selectedOrganization]: 'organization',
    [AUTH_STORAGE_KEYS.selectedBranch]: 'branch'
};

export const AUTH_SESSION_RESET_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.accessToken,
    AUTH_STORAGE_KEYS.accessTokenTimestamp,
    AUTH_STORAGE_KEYS.user,
    AUTH_STORAGE_KEYS.organizations,
    AUTH_STORAGE_KEYS.access,
    AUTH_STORAGE_KEYS.impersonatorAccessToken,
    AUTH_STORAGE_KEYS.selectedOrganization,
    AUTH_STORAGE_KEYS.selectedBranch
] as const;

export const AUTH_REFRESH_FAILURE_RESET_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.accessToken,
    AUTH_STORAGE_KEYS.accessTokenTimestamp,
    AUTH_STORAGE_KEYS.user,
    AUTH_STORAGE_KEYS.organizations,
    AUTH_STORAGE_KEYS.access,
    AUTH_STORAGE_KEYS.impersonatorAccessToken
] as const;

export const AUTH_IMPERSONATION_SELECTION_KEYS: readonly AuthStorageKey[] = [
    AUTH_STORAGE_KEYS.impersonatorSelectedOrganization,
    AUTH_STORAGE_KEYS.impersonatorSelectedBranch
] as const;

function hasStorage(): boolean {
    return typeof localStorage !== 'undefined';
}

function getBrowserWindow(): (Window & { settings?: HostWindowSettings; __HOST_SETTINGS__?: HostWindowSettings }) | null {
    if (typeof window === 'undefined') {
        return null;
    }

    return window as Window & { settings?: HostWindowSettings; __HOST_SETTINGS__?: HostWindowSettings };
}

function readAppSettingsFromStorage(): HostWindowSettings | null {
    if (!hasStorage()) {
        return null;
    }

    const raw = localStorage.getItem(APP_SETTINGS_STORAGE_KEY);
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed as HostWindowSettings : null;
    } catch {
        localStorage.removeItem(APP_SETTINGS_STORAGE_KEY);
        return null;
    }
}

function writeAppSettingsToStorage(settings: HostWindowSettings): void {
    if (!hasStorage()) {
        return;
    }

    try {
        localStorage.setItem(APP_SETTINGS_STORAGE_KEY, JSON.stringify(settings || {}));
    } catch {
        // Ignore storage serialization errors.
    }
}

function getWindowSettings(): HostWindowSettings | null {
    const browserWindow = getBrowserWindow();
    if (browserWindow) {
        const settings = browserWindow.settings;
        if (settings && typeof settings === 'object') {
            return settings;
        }

        const legacySettings = browserWindow.__HOST_SETTINGS__;
        if (legacySettings && typeof legacySettings === 'object') {
            return legacySettings;
        }
    }

    return readAppSettingsFromStorage();
}

function setWindowSettings(settings: HostWindowSettings): void {
    const browserWindow = getBrowserWindow();
    if (!browserWindow) {
        return;
    }

    browserWindow.settings = settings;
    browserWindow.__HOST_SETTINGS__ = settings;
}

function isAppSettingsKey(key: AuthStorageKey): boolean {
    return !!APP_SETTINGS_KEY_MAP[key];
}

function getAppSettingsPath(key: AuthStorageKey): string {
    return APP_SETTINGS_KEY_MAP[key] ?? '';
}

function getValueAtPath(source: any, path: string): unknown {
    if (!source || !path) {
        return null;
    }

    const segments = path.split('.');
    let cursor: any = source;

    for (const segment of segments) {
        if (!cursor || typeof cursor !== 'object' || !(segment in cursor)) {
            return null;
        }

        cursor = cursor[segment];
    }

    return cursor ?? null;
}

function setValueAtPath(target: any, path: string, value: unknown): void {
    const segments = path.split('.');
    let cursor = target;

    for (let i = 0; i < segments.length - 1; i += 1) {
        const segment = segments[i];
        if (!cursor[segment] || typeof cursor[segment] !== 'object') {
            cursor[segment] = {};
        }
        cursor = cursor[segment];
    }

    cursor[segments[segments.length - 1]] = value;
}

function removeValueAtPath(target: any, path: string): void {
    const segments = path.split('.');
    let cursor = target;

    for (let i = 0; i < segments.length - 1; i += 1) {
        const segment = segments[i];
        if (!cursor[segment] || typeof cursor[segment] !== 'object') {
            return;
        }
        cursor = cursor[segment];
    }

    delete cursor[segments[segments.length - 1]];
}

function getAppSettings(): HostWindowSettings {
    return { ...(getWindowSettings() || {}) };
}

function persistAppSettings(settings: HostWindowSettings): void {
    setWindowSettings(settings);
    writeAppSettingsToStorage(settings);
}

function getAppSettingsStringValue(key: AuthStorageKey): string {
    const path = getAppSettingsPath(key);
    if (!path) {
        return '';
    }

    const settings = getAppSettings();
    if (!settings) {
        return '';
    }

    const value = getValueAtPath(settings, path);

    if (value == null) {
        return '';
    }

    if (typeof value === 'string') {
        return value;
    }

    try {
        return JSON.stringify(value);
    } catch {
        return '';
    }
}

function setAppSettingsValue(key: AuthStorageKey, value: unknown): void {
    const path = getAppSettingsPath(key);
    if (!path) {
        return;
    }

    const settings = getAppSettings();

    if (value === '' || value == null) {
        removeValueAtPath(settings, path);
    } else {
        setValueAtPath(settings, path, value);
    }

    persistAppSettings(settings);
}

function parseJsonSafe<T>(raw: string): T | null {
    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}

function getAppSettingsRawValue(key: AuthStorageKey): unknown {
    const path = getAppSettingsPath(key);
    if (!path) {
        return null;
    }

    return getValueAtPath(getAppSettings(), path);
}

function isAppSettingsJsonKey(key: AuthStorageKey): boolean {
    return key === AUTH_STORAGE_KEYS.hostApp
        || key === AUTH_STORAGE_KEYS.hostSupport
        || key === AUTH_STORAGE_KEYS.user
        || key === AUTH_STORAGE_KEYS.selectedOrganization
        || key === AUTH_STORAGE_KEYS.selectedBranch;
}

export const AuthStorage = {
    get(key: AuthStorageKey): string {
        if (isAppSettingsKey(key)) {
            return getAppSettingsStringValue(key);
        }

        if (!hasStorage()) {
            return '';
        }

        return localStorage.getItem(key) ?? '';
    },

    set(key: AuthStorageKey, value: string): void {
        if (isAppSettingsKey(key)) {
            if (isAppSettingsJsonKey(key)) {
                const parsed = parseJsonSafe<unknown>(value);
                setAppSettingsValue(key, parsed ?? value);
            } else {
                setAppSettingsValue(key, value);
            }
            return;
        }

        if (!hasStorage()) {
            return;
        }

        localStorage.setItem(key, value);
    },

    remove(key: AuthStorageKey): void {
        if (isAppSettingsKey(key)) {
            setAppSettingsValue(key, null);
            return;
        }

        if (!hasStorage()) {
            return;
        }

        localStorage.removeItem(key);
    },

    removeMany(keys: readonly AuthStorageKey[]): void {
        const canUseStorage = hasStorage();

        keys.forEach((key) => {
            if (isAppSettingsKey(key)) {
                setAppSettingsValue(key, null);
                return;
            }

            if (canUseStorage) {
                localStorage.removeItem(key);
            }
        });
    },

    setJson(key: AuthStorageKey, value: unknown): void {
        if (isAppSettingsKey(key)) {
            setAppSettingsValue(key, value);
            return;
        }

        if (!hasStorage()) {
            return;
        }

        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // Ignore storage serialization errors.
        }
    },

    getJson<T>(key: AuthStorageKey): T | null {
        if (isAppSettingsKey(key)) {
            const value = getAppSettingsRawValue(key);
            if (value == null) {
                return null;
            }

            if (typeof value === 'string') {
                const parsed = parseJsonSafe<T>(value);
                return parsed ?? (value as unknown as T);
            }

            return value as T;
        }

        if (!hasStorage()) {
            return null;
        }

        const raw = localStorage.getItem(key);
        if (!raw) {
            return null;
        }

        try {
            return JSON.parse(raw) as T;
        } catch {
            localStorage.removeItem(key);
            return null;
        }
    }
} as const;
